// Показателите на „Сторно".
//
// „След кухня" се показва САМО когато има какво да се твърди. Етапът не е
// поле в базата — извежда се от одита на кухненските преходи. Заведение без
// монитори няма нито един такъв запис и там „0 след кухня" би било измислица,
// а не факт, затова кутийката просто липсва.

import { useTranslation } from "react-i18next";

import { fmtEuro } from "../../../../utils/currency";
import { ReportKpiCard } from "../ReportKpiCard";
import { deltaPercent } from "../reportDelta";
import { pluralLabel } from "../reportPlural";
import type { CancellationsSummary } from "./cancellationsMath";

/** Минус знак U+2212 — сторното е отнета сума. */
const MINUS = "−";

type Props = {
  summary: CancellationsSummary;
  previous: CancellationsSummary | null;
  /** Оборотът за периода — за „X % от брутото". */
  revenue: number;
  isNarrow: boolean;
};

export const CancellationsKpis = ({
  summary,
  previous,
  revenue,
  isNarrow,
}: Props) => {
  const { t } = useTranslation();

  // Брутото = платеното + сторнираното: делът отговаря на „колко от
  // маркираното е отпаднало". Оборотът брои САМО закрити сметки — период без
  // нито една закрита сметка (или сторно само по отворени маси) би дал 100 %
  // „от брутото", което е артефакт на знаменателя, не факт. Тогава редът под
  // сумата казва просто за какво е тя.
  const shareOfGross =
    revenue > 0 ? (summary.total / (revenue + summary.total)) * 100 : null;

  // Има ли изобщо кухненски записи в този период.
  const kitchenKnown = summary.rows > 0 && summary.kitchenUnknown < summary.rows;

  return (
    // Класовете са ЦЕЛИ литерали: Tailwind сканира изходния текст и не вижда
    // име, сглобено с template literal — така лентата тихо пада на 2 колони.
    <div
      className={`grid gap-2.5 ${
        isNarrow
          ? "grid-cols-2"
          : kitchenKnown
            ? "grid-cols-2 lg:grid-cols-[1.2fr_1.3fr_1fr_1fr]"
            : "grid-cols-2 lg:grid-cols-[1.2fr_1.3fr_1fr]"
      }`}
    >
      <ReportKpiCard
        kicker={t("reportsPage.voidsRowsKicker")}
        value={String(summary.rows)}
        sub={
          // Броят БРАК стои до броя сметки: старият екран имаше тристепенен
          // избор „Общо/Брак/Сторно" с броячи, а филтърът в колоната показва
          // само единия наведнъж.
          summary.defective > 0
            ? `${pluralLabel(
                t,
                summary.bills,
                "reportsPage.voidsInBillsOne",
                "reportsPage.voidsInBills",
              )} · ${t("reportsPage.voidsScrapCount", { count: summary.defective })}`
            : pluralLabel(
                t,
                summary.bills,
                "reportsPage.voidsInBillsOne",
                "reportsPage.voidsInBills",
              )
        }
        delta={previous ? deltaPercent(summary.rows, previous.rows) : undefined}
        invertDelta
        hero
      />

      <ReportKpiCard
        kicker={t("reportsPage.voidsValue")}
        value={
          summary.total > 0 ? `${MINUS}${fmtEuro(summary.total)}` : fmtEuro(0)
        }
        sub={
          shareOfGross === null
            ? t("reportsPage.voidsValueSub")
            : t("reportsPage.voidsShareOfGross", {
                share: shareOfGross.toFixed(2),
              })
        }
        delta={previous ? deltaPercent(summary.total, previous.total) : undefined}
        invertDelta
        hot={summary.total > 0}
        hero
      />

      {kitchenKnown ? (
        <ReportKpiCard
          kicker={t("reportsPage.voidsAfterKitchen")}
          value={String(summary.afterKitchen)}
          sub={t("reportsPage.voidsRealLoss")}
          hot={summary.afterKitchen > 0}
        />
      ) : null}

      <ReportKpiCard
        kicker={t("reportsPage.voidsPerRow")}
        value={fmtEuro(summary.average)}
        sub={t("reportsPage.voidsPerRowSub")}
      />
    </div>
  );
};
